import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';
import { SolidColors } from '@provider-portal/common/styles/bolt-uikit-colors';
import { performNavigate } from '@provider-portal/common/utils/navigation';

import { smartPromotionRoutes } from '../../providers/apiClient/routes';
import bannerIcon from './assets/sp-banner-icon.svg';

const styles = {
  baseContainer: {
    width: '100%',
    backgroundColor: `${SolidColors.purple500}14`,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    padding: '24px',
    gap: '40px',
    backgroundImage: `url('${bannerIcon}')`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom right -11px',
    backgroundSize: `140px 140px`,
  },
  infoContainer: {
    maxWidth: '90%',
  },
  button: {
    fontSize: '14px',
    padding: '8px 16px',
    mt: '16px',
  },
  growth: {
    color: SolidColors.green700,
  },
} as const;

interface PromotionSettingsProps {
  netRevenueUplift?: number;
  onButtonClick: () => void;
}

export const SmartPromotionsBanner: React.FC<PromotionSettingsProps> = ({
  netRevenueUplift,
  onButtonClick,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const goToSmartPromotionsList = useCallback(() => {
    onButtonClick();
    performNavigate(history, smartPromotionRoutes.create);
  }, [history, onButtonClick]);

  const shouldShowNetUpliftStats = useMemo(() => {
    return Boolean(netRevenueUplift) && netRevenueUplift > 0;
  }, [netRevenueUplift]);

  return (
    <Box sx={styles.baseContainer}>
      <Box sx={styles.infoContainer}>
        <Typography variant="h6">
          {intl.formatMessage({ id: 'components.smartPromotionsBanner.title' })}
          {shouldShowNetUpliftStats && (
            <span style={styles.growth}>
              {intl.formatMessage(
                { id: 'components.smartPromotionsBanner.growth' },
                { value: netRevenueUplift },
              )}
            </span>
          )}
        </Typography>
        <Box>{intl.formatMessage({ id: 'components.smartPromotionsBanner.description' })}</Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={styles.button}
            onClick={goToSmartPromotionsList}
          >
            {intl.formatMessage({ id: 'components.smartPromotionsBanner.button' })}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
